import { ProfileAvatar } from './ProfileAvatar';
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin';
import { Crown } from '@/components/icons';
import { cn } from '@/lib/utils';

interface ProfileAvatarWithBadgeProps {
  photoS3Key?: string | null;
  fallbackText: string;
  className?: string;
  avatarColor?: string; // Background color for the avatar
  role?: string; // Optional role prop to check if this specific user is admin
  isAdmin?: boolean; // Direct isAdmin flag from crew data
  useCurrentUser?: boolean; // If true, check the current logged-in user
  /** Size/offset overrides for the badge so it scales with small avatars. */
  badgeClassName?: string;
  iconClassName?: string;
}

export function ProfileAvatarWithBadge({
  photoS3Key,
  fallbackText,
  className = '',
  avatarColor,
  role,
  isAdmin,
  useCurrentUser = true,
  badgeClassName = '-top-0.5 -right-0.5 p-1',
  iconClassName = 'max-h-3.5 max-w-3.5',
}: ProfileAvatarWithBadgeProps) {
  const { isAdmin: currentUserIsAdmin } = useIsAdmin();

  // Determine if badge should show
  let showBadge = false;

  if (isAdmin !== undefined) {
    // Use direct isAdmin prop if provided
    showBadge = isAdmin;
  } else if (useCurrentUser) {
    // Check if current logged-in user is admin
    showBadge = currentUserIsAdmin;
  } else if (role) {
    // Check if provided role is admin
    const roleLower = role.toLowerCase();
    showBadge = roleLower === 'admin';
  }

  return (
    <div className="relative inline-block">
      <ProfileAvatar
        className={className}
        photoS3Key={photoS3Key}
        avatarColor={avatarColor}
        initials={fallbackText}
        fallbackClassName="text-background"
      />

      {/* Admin Badge - positioned at top-right */}
      {showBadge && (
        <div
          className={cn(
            'absolute flex items-center justify-center rounded-full bg-background-brand-bold',
            badgeClassName,
          )}
        >
          <Crown className={cn('text-content-primary-inverse', iconClassName)} />
        </div>
      )}
    </div>
  );
}